import { GroundType } from 'models/ground';
import React from 'react';
import {
  Circle, Group, Line, Path, Text,
} from 'react-konva';
import {
  DEFAULT_PITCH_LINE_COLOR,
  DEFAULT_PITCH_STROKEWIDTH,
  FOOTBALL_CENTER_RADIUS,
  FOOTBALL_DISK_OFFSET,
  FOOTBALL_DISK_RADIUS,
  FOOTBALL_GOAL_AREA_HEIGHT,
  FOOTBALL_GOAL_AREA_HEIGHT_SMALL,
  FOOTBALL_GOAL_AREA_WIDTH,
  FOOTBALL_GOAL_AREA_WIDTH_SMALL,
} from '../constants';
import {
  rotate, flipPoint, computeAngle, flipX, flipY, describeArc,
} from '../utils';
import Vertices from '../Vertices';
import { GroundGeometry } from '../../../../types/webPlayer';

function Area(props: {
  vertexA: { x: number; y: number };
  vertexC: { x: number; y: number };
  height: number;
  center: { x: number; y: number };
  scale: number;
  flipped?: boolean;
}) {
  const {
    vertexA, vertexC, height, scale, flipped, center,
  } = props;

  const scaledAreaWidth = 13 * scale;
  const scaledAreaHeight = 25 * scale;
  const scaledAreaWidthSm = 4 * scale;
  const scaledAreaHeightSm = FOOTBALL_GOAL_AREA_HEIGHT_SMALL * scale;
  const scaledDiskOffset = 10 * scale;
  const scaledDiskRadius = FOOTBALL_DISK_RADIUS * scale;

  return (
    <Group>
      <Line
        points={
          flipped
            ? [
              flipX({ x: vertexA.x, y: vertexA.y + scaledAreaHeight / 2 - height / 2 }, center),
              flipY({ x: vertexA.x, y: vertexA.y + scaledAreaHeight / 2 - height / 2 }, center),
              flipX({ x: vertexA.x + scaledAreaWidth, y: vertexA.y + scaledAreaHeight / 2 - height / 2 }, center),
              flipY({ x: vertexA.x + scaledAreaWidth, y: vertexA.y + scaledAreaHeight / 2 - height / 2 }, center),
              flipX({ x: vertexA.x + scaledAreaWidth, y: vertexA.y - scaledAreaHeight / 2 - height / 2 }, center),
              flipY({ x: vertexA.x + scaledAreaWidth, y: vertexA.y - scaledAreaHeight / 2 - height / 2 }, center),
              flipX({ x: vertexA.x, y: vertexA.y - scaledAreaHeight / 2 - height / 2 }, center),
              flipY({ x: vertexA.x, y: vertexA.y - scaledAreaHeight / 2 - height / 2 }, center),
            ]
            : [
              vertexA.x,
              vertexA.y + scaledAreaHeight / 2 - height / 2,
              vertexA.x + scaledAreaWidth,
              vertexA.y + scaledAreaHeight / 2 - height / 2,
              vertexA.x + scaledAreaWidth,
              vertexA.y - scaledAreaHeight / 2 - height / 2,
              vertexA.x,
              vertexA.y - scaledAreaHeight / 2 - height / 2,
            ]
        }
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
      />
      <Line
        points={
          flipped
            ? [
              flipX({ x: vertexA.x, y: vertexA.y + scaledAreaHeightSm / 2 - height / 2 }, center),
              flipY({ x: vertexA.x, y: vertexA.y + scaledAreaHeightSm / 2 - height / 2 }, center),
              flipX({ x: vertexA.x + scaledAreaWidthSm, y: vertexA.y + scaledAreaHeightSm / 2 - height / 2 }, center),
              flipY({ x: vertexA.x + scaledAreaWidthSm, y: vertexA.y + scaledAreaHeightSm / 2 - height / 2 }, center),
              flipX({ x: vertexA.x + scaledAreaWidthSm, y: vertexA.y - scaledAreaHeightSm / 2 - height / 2 }, center),
              flipY({ x: vertexA.x + scaledAreaWidthSm, y: vertexA.y - scaledAreaHeightSm / 2 - height / 2 }, center),
              flipX({ x: vertexA.x, y: vertexA.y - scaledAreaHeightSm / 2 - height / 2 }, center),
              flipY({ x: vertexA.x, y: vertexA.y - scaledAreaHeightSm / 2 - height / 2 }, center),
            ]
            : [
              vertexA.x,
              vertexA.y + scaledAreaHeightSm / 2 - height / 2,
              vertexA.x + scaledAreaWidthSm,
              vertexA.y + scaledAreaHeightSm / 2 - height / 2,
              vertexA.x + scaledAreaWidthSm,
              vertexA.y - scaledAreaHeightSm / 2 - height / 2,
              vertexA.x,
              vertexA.y - scaledAreaHeightSm / 2 - height / 2,
            ]
        }
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
      />
      <Circle
        x={flipped ? vertexC.x - scaledDiskOffset : vertexA.x + scaledDiskOffset}
        y={vertexA.y - height / 2}
        radius={scaledDiskRadius < 0 ? scaledDiskRadius * -1 : scaledDiskRadius}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
        fill={DEFAULT_PITCH_LINE_COLOR}
      />

    </Group>
  );
}

export default function GaelicFootball(props: { groundData: GroundType;
  scale: number;
  vertices: boolean;
  groundGeometry: GroundGeometry;
}) {
  const { groundData, scale, vertices, groundGeometry } = props;

  const dataScale = props.scale / groundGeometry.scale;

  let vertexA = {
    x:
      rotate(flipPoint({ x: groundData.vertexAX?.value, y: groundData.vertexAY?.value }, groundData), computeAngle(groundData)).x
      * scale,
    y:
      rotate(flipPoint({ x: groundData.vertexAX?.value, y: groundData.vertexAY?.value }, groundData), computeAngle(groundData)).y
      * scale,
  };
  let vertexC = {
    x:
      rotate(flipPoint({ x: groundData.vertexCX?.value, y: groundData.vertexCY?.value }, groundData), computeAngle(groundData)).x
      * scale,
    y:
      rotate(flipPoint({ x: groundData.vertexCX?.value, y: groundData.vertexCY?.value }, groundData), computeAngle(groundData)).y
      * scale,
  };

  if (vertexA.x > vertexC.x) {
    const swap = vertexC;
    vertexC = vertexA;
    vertexA = swap;
  }

  const width = vertexC.x - vertexA.x;
  const height = vertexA.y - vertexC.y;

  const center = { x: vertexA.x + width / 2, y: vertexA.y - height / 2 };

  const scaledCenterRadius = FOOTBALL_CENTER_RADIUS * scale;
  const scaledDiskRadius = FOOTBALL_DISK_RADIUS * scale;

  const linesPositionFactor = [0.09, 0.137, 0.313, 0.451, 0.547, 0.686, 0.864, 0.91];

  return (
    <Group scaleX={dataScale} scaleY={dataScale}>
      {vertices ? <Vertices A={vertexA} C={vertexC} /> : null}
      {/* border */}
      <Line
        fill={groundData.groundSurfaceColor ? groundData.groundSurfaceColor : '#00000000'}
        points={[vertexA.x, vertexA.y, vertexC.x, vertexA.y, vertexC.x, vertexC.y, vertexA.x, vertexC.y]}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
        closed
      />
      {/* left area */}
      <Area vertexA={vertexA} vertexC={vertexC} height={height} center={center} scale={scale} />
      {/* halfcourt */}
      <Line
        points={[vertexA.x + width / 2, vertexA.y, vertexC.x - width / 2, vertexC.y]}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
      />
      <Path
        data={describeArc(
          vertexA.x + 52.5,
          vertexA.y - height / 2,
          scaledCenterRadius * 2,
          20,
          160,
        )}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
      />
      <Path
        data={describeArc(
          vertexA.x + width - 52.5,
          vertexA.y - height / 2,
          scaledCenterRadius * 2,
          199,
          341,
        )}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
      />
      {linesPositionFactor.map((l) => (
        <Line
          points={[vertexA.x + width * l, vertexA.y, vertexA.x + width * l, vertexA.y - height]}
          stroke={DEFAULT_PITCH_LINE_COLOR}
          strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
        />
      ))}

      {/* rightarea */}
      <Area vertexA={vertexA} vertexC={vertexC} height={height} center={center} scale={scale} flipped />
    </Group>
  );
}
